<template>
  <div class="container homebase-container pt-5 px-0">
    <div v-if="isLoading">
      <loading-spinner-small />
    </div>
    <div v-else>
      <div class="mt-4 alert alert-error" v-if="isErrored">
        {{ CONSTANTS.SERVER_ERROR_MESSAGE }}
      </div>
      <div v-else>
        <div class="table-wrapper">
          <vue-good-table
            ref="bookmarksTable"
            :key="componentKey"
            :columns="columns"
            :rows="bookmarks"
            class="table-word-wrap"
            :search-options="searchOptions"
            :sort-options="sortOptions"
            :pagination-options="{
              enabled: false,
            }"
            styleClass="vgt-table"
            :row-style-class="rowStyleClassFn"
            @sort-change="onSortChange"
          >
            <template #emptystate>
              <div class="emptystate-text">No bookmarks to display</div>
            </template>
            <template #table-row="props">
              <span v-if="props.column.field === 'actions'">
                <div>
                  <pin-bookmark
                    class="d-inline-block pr-2"
                    :bookmark-data="props.row"
                    @bookmarkPinned="handleBookmarkPinned"
                    @bookmarkUnpinned="handleBookmarkUnpinned"
                    :pinning-disabled="pinningDisabled"
                    :success-msg="getSuccessMessage(props.row.id)"
                  />
                  <AutodeskOneToggleBookmarkPage
                    :bookmarkRowValue="props.row"
                    class="mr-2 d-inline-block"
                    @bookmarkRemoved="triggerBookmarkRemoval(props.row)"
                    @bookmarkRemovalUndo="cancelBookmarkRemoval(props.row)"
                  />
                </div>
              </span>
              <span v-else-if="props.column.field == 'title'">
                <div class="truncate-title">
                  <a
                    :href="props.row.url"
                    :target="getBookmarkTarget(props.row.url)"
                  >
                    {{ props.row.title }}
                  </a>
                </div>
              </span>
            </template>
            <template #table-column="props">
              <span v-if="props.column.label == 'Pins'">
                {{ pinsColumnLabel }}
              </span>
              <span v-else>
                {{ props.column.label }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as CONSTANTS from "@/store/constants";
import { VueGoodTable } from "vue-good-table-next";
import "vue-good-table-next/dist/vue-good-table-next.css";
import { mapGetters, mapActions } from "vuex";

import LoadingSpinnerSmall from "@/components/LoadingSpinnerSmall"
import AutodeskOneBookmarksMixin from "@/mixins/AutodeskOneBookmarksMixin";
import HomebaseMixpanelMixin from "@/mixins/HomebaseMixpanelMixin";
import AutodeskOneToggleBookmarkPage from "@/components/homebase/pages/AutodeskOneToggleBookmarkPage";
import PinBookmark from "@/components/homebase/bookmarks/PinBookmark";

export default {
  name: "autodesk-one-bookmarks-page",
  mixins: [HomebaseMixpanelMixin, AutodeskOneBookmarksMixin],
  components: {
    LoadingSpinnerSmall,
    VueGoodTable,
    AutodeskOneToggleBookmarkPage,
    PinBookmark
  },
  data() {
    return {
      CONSTANTS,
      isLoading: false,
      isErrored: false,
      bookmarks: [],
      bookmark: {},
      componentKey: 0,
      pinnedCount: 0,

      successMsgMap: {},

      // sort
      sortOptions: {
        enabled: true,
      },
      // search
      searchOptions: {
        enabled: true,
        skipDiacritics: true,
        placeholder: "Search bookmarks",
      },
      // columns
      columns: [
        {
          label: "Bookmark name",
          field: "title",
          type: "string",
          thClass: "first-column width-sm",
          tdClass: "first-column width-sm",
          width: "60%",
        },
        {
          label: "Source",
          field: "sourceValue",
          type: "string",
          thClass: "hide-field-sm",
          tdClass: "hide-field-sm truncate",
          width: "32%",
        },
        {
          label: "Pins",
          field: "actions",
          thClass: "th-actions text-nowrap width-sm",
          tdClass: "td-actions text-nowrap width-sm",
          width: "8%",
          sortFn: this.sortPinsFn,
        },
      ],
    };
  },
  created() {
    this.initializeBookmarks();
  },
  computed: {
    ...mapGetters(["successMessage", "errorMessage", "user"]),
    formattedRows() {
      return this.bookmarks;
    },
    isTableEmpty() {
      return !this.bookmarks || this.bookmarks.length == 0;
    },
    pinsColumnLabel() {
      return `Pins (${this.pinnedCount}/6)`;
    },
    pinningDisabled() {
      return this.pinnedCount >= 6;
    },
  },
  methods: {
    ...mapActions(["setBannerSuccessMessage", "setBannerErrorMessage"]),

    async initializeBookmarks() {
      this.isLoading = true;
      try {
        let res = await this.getAllBookmarks();
        let bookmarks = this.filterBookmarks(res);

        let pinnedBookmarks = JSON.parse(JSON.stringify(bookmarks));
        pinnedBookmarks = pinnedBookmarks.filter((b) => b.isPin);
        this.pinnedCount = pinnedBookmarks.length;

        let unpinnedBookmarks = bookmarks.filter((b) => !b.isPin);
        let formattedBookmarks = pinnedBookmarks.concat(unpinnedBookmarks);

        this.setBookmarks(formattedBookmarks)
      } catch (e) {
        this.isErrored = true;
      } finally {
        this.isLoading = false;
      }
    },

    rowStyleClassFn(row) {
      return row.isPin ? "pinned-bg" : "";
    },

    handleViewClick(bookmarkData) {
      let title = bookmarkData.title;
      let url = bookmarkData.url;
      let subEvent = `${title} - ${url}`;
      this.trackViewBookmark(subEvent);
    },

    triggerBookmarkRemoval(bookmarkData) {
      let bookmarks = this.getProcessedBookmarks();
      let i = bookmarks.findIndex((b) => b.id === bookmarkData.id);
      if (i > -1) bookmarks[i].showUndo = true;
      this.setBookmarks(bookmarks);

      setTimeout(() => {
        bookmarks = this.getProcessedBookmarks();
        let i = bookmarks.findIndex((b) => b.id === bookmarkData.id);
        if (i > -1 && bookmarks[i].showUndo) this.deleteBookmark(bookmarks[i]);
      }, 10000);
    },

    cancelBookmarkRemoval(bookmarkData) {
      let bookmarks = this.getProcessedBookmarks();
      let i = bookmarks.findIndex((b) => b.id === bookmarkData.id);
      if (i > -1) bookmarks[i].showUndo = false;
      this.setBookmarks(bookmarks);
    },

    deleteBookmark(bookmarkData) {
      let bookmarks = this.getProcessedBookmarks();
      bookmarks = bookmarks.filter((b) => b.id != bookmarkData.id);
      this.setBookmarks(bookmarks);

      if (bookmarkData.isPin) this.pinnedCount--;
    },

    handleBookmarkPinned(payload) {
      let bookmarks = this.getProcessedBookmarks();
      let i = bookmarks.findIndex((b) => b.id === payload.id);
      if (i > -1) bookmarks[i] = payload;

      this.setBookmarks(bookmarks);
      this.setSuccessMessage(payload.id, "Bookmark pinned.");
      this.pinnedCount++;
    },

    handleBookmarkUnpinned(payload) {
      let bookmarks = this.getProcessedBookmarks();
      let i = bookmarks.findIndex((b) => b.id === payload.id);
      if (i > -1) bookmarks[i] = payload;
      let formattedBookmarks = bookmarks;

      this.setBookmarks(formattedBookmarks);
      this.setSuccessMessage(payload.id, "Bookmark unpinned.");
      this.pinnedCount--;
    },

    onSortChange(params) {
      this.sortOptions.initialSortBy = params[0];
    },

    sortPinsFn(x, y, col, rowX, rowY) {
      if (rowX.isPin && !rowY.isPin) return -1;
      if (!rowX.isPin && rowY.isPin) return 1;
      return 0;
    },

    getProcessedBookmarks() {
      return JSON.parse(
        JSON.stringify(this.$refs.bookmarksTable.processedRows[0].children)
      );
    },

    setBookmarks(formattedBookmarks) {
      this.bookmarks = null;
      this.bookmarks = formattedBookmarks;
      this.componentKey++;
    },

    setSuccessMessage(id, msg) {
      this.successMsgMap[id] = msg;
      if (msg.length > 0) {
        setTimeout(() => {
          if (this.successMsgMap[id] === msg) this.successMsgMap[id] = "";
          this.componentKey++;
        }, 5000);
      }
    },

    getSuccessMessage(id) {
      return this.successMsgMap[id];
    },
  },
};
</script>

<style scoped src="../../../../static/aone-theme.css"></style>
<style scoped src="../../../../static/vgt-styles.css"></style>
<style scoped>
:deep(.vgt-wrap),
.vgt-wrap :deep(.vgt-inner-wrap),
.vgt-wrap :deep(.vgt-table),
.vgt-wrap :deep(.vgt-table.bordered) {
  width: 100%;
  box-shadow: none !important;
  border: none !important;
  background: transparent !important;
}

.vgt-wrap :deep(.vgt-table) {
  border-left: none !important;
  border-right: none !important;
}

.vgt-wrap :deep(.vgt-table th:first-child),
.vgt-wrap :deep(.vgt-table td:first-child) {
  border-left: none !important;
}

.vgt-wrap :deep(.vgt-table th:last-child),
.vgt-wrap :deep(.vgt-table td:last-child) {
  border-right: none !important;
}

a {
  font-family: artifaktElementBold;
}
a:hover {
  cursor: pointer;
  text-decoration: none !important;
}
.bookmark-icon {
  font-size: 20px;
  color: var(--bookmark-color) !important;
}
.truncate-title {
  white-space: nowrap;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  max-width: 90% !important;
}
.header-wrapper {
  background-color: #fcfcfc;
}
.page-description {
  padding-top: 25px;
  padding-bottom: 100px;
}
/* .table-wrapper :deep(.vgt-table th.sorting:after) {
  border-bottom: none !important;
} */
.table-wrapper :deep(.vgt-wrap__footer) {
  display: none !important;
}
.table-wrapper :deep(.first-column) {
  padding-left: 15px !important;
}
.vgt-wrap :deep(.pinned-bg) {
  background: #f8f8fd;
  border-bottom: 1px solid white !important;
}
.table-wrapper :deep(.pinned-links-text) {
  color: rgba(63, 64, 226, 1);
  font-size: 14px;
  font-family: artifaktElementBold;
}

/* Hide default vue-good-table sort icons */
:deep(.vgt-wrap .vgt-table th span.sort-icon),
:deep(.vgt-wrap .vgt-table th .sort-icon),
:deep(.vgt-wrap .vgt-table th svg),
:deep(.vgt-wrap .vgt-table th i.sort-icon) {
  display: none !important;
  visibility: hidden !important;
}

/* Hide the :before pseudo-elements (library default) */
:deep(.vgt-wrap .vgt-table thead th:before),
:deep(.vgt-wrap .vgt-table th.sorting:before),
:deep(.vgt-wrap .vgt-table th.sortable:before) {
  content: none !important;
  display: none !important;
  border: none !important;
}

/* Hide the visual content of sort buttons but keep them clickable */
:deep(.vgt-wrap .vgt-table thead th button) {
  position: absolute !important;
  opacity: 0 !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  cursor: pointer !important;
}

:deep(.vgt-wrap .vgt-table thead th) {
  position: relative !important;
  overflow: visible !important;
}

</style>


